import React, { useEffect, useState } from "react";
import {
  VariantControl,
  VariantDot,
  VariantType,
} from "../../components/variant/VariantControl";
import { NewPropertyState } from "../../../usePropertyStateV2";
import { ComboBoxOptionType, DiscreteInput } from "../../components/input";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  FLEX_DIRECTION_OPTIONS,
  FLEX_WRAP_OPTIONS,
} from "../../components/variant/variantOptions";
import { PropertyType } from "@/tailwind/tailwindUtils";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface FlexDirectionWrapProps {
  flexWrapState: NewPropertyState;
  flexDirectionState: NewPropertyState;
  justifyContentState: NewPropertyState;
  alignItemsState: NewPropertyState;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const FlexDirectionWrapField: React.FC<FlexDirectionWrapProps> = React.memo(
  ({
    flexWrapState,
    flexDirectionState,
    justifyContentState,
    alignItemsState,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
  }) => {
    return (
      <div className="flex flex-col items-start gap-1">
        <div className="flex flex-col items-start justify-between gap-1 relative">
          <div className="text-xs text-text-secondary font-normal">
            Direction + Wrap
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 relative w-full">
          <div className="flex flex-col gap-2 w-full">
            <ContextMenu>
              <ContextMenuTrigger asChild>
                <div className="min-w-28 max-w-32 w-full relative">
                  <VariantDot
                    valueByVariant={flexDirectionState.valueByVariant}
                  />
                  <DiscreteInput
                    options={FLEX_DIRECTION_OPTIONS}
                    onOptionSelected={(discreteOption) => {
                      pickTailwindClassForProperty({
                        option: discreteOption.option,
                        propertyType: PropertyType.FLEX_DIRECTION,
                        setTemporaryDraggingValue:
                          flexDirectionState.setTemporaryDraggingValue,
                      });
                    }}
                    selected={flexDirectionState.propertyValue}
                    set={flexDirectionState.isSet}
                    onDeselect={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FLEX_DIRECTION,
                        setTemporaryDraggingValue:
                          flexDirectionState.setTemporaryDraggingValue,
                      });
                    }}
                  />
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent className="p-0 border-none">
                <VariantControl
                  valueByVariant={flexDirectionState.valueByVariant as any}
                  onVariantChange={() => {}}
                  onClearVariant={(variant: VariantType) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FLEX_DIRECTION,
                      setTemporaryDraggingValue:
                        flexDirectionState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  currentFlexDirection={flexDirectionState.propertyValue}
                  currentJustifyContent={justifyContentState.propertyValue}
                  currentAlignItems={alignItemsState.propertyValue}
                  tooltipContent="Variants"
                  className="mr-1"
                  controlType="direction-wrap"
                  children={(variant, expanded, setExpanded) => {
                    if (!expanded || !variant) return null;
                    const variantValue =
                      flexDirectionState.valueByVariant[variant]?.value || "";
                    return (
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                          <DiscreteInput
                            options={FLEX_DIRECTION_OPTIONS}
                            onOptionSelected={(discreteOption) => {
                              pickTailwindClassForProperty({
                                option: discreteOption.option,
                                propertyType: PropertyType.FLEX_DIRECTION,
                                setTemporaryDraggingValue:
                                  flexDirectionState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            selected={variantValue}
                            set={!!variantValue}
                            onDeselect={() => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.FLEX_DIRECTION,
                                setTemporaryDraggingValue:
                                  flexDirectionState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                          />
                        </div>
                        <div className="flex flex-row gap-2 mt-2">
                          <div
                            className="text-xs text-secondary-n-500 hover:text-red-500"
                            onClick={() => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.FLEX_DIRECTION,
                                setTemporaryDraggingValue:
                                  flexDirectionState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                          >
                            Clear
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </ContextMenuContent>
            </ContextMenu>

            <ContextMenu>
              <ContextMenuTrigger asChild>
                <div className="w-24 relative">
                  <VariantDot valueByVariant={flexWrapState.valueByVariant} />
                  <DiscreteInput
                    options={FLEX_WRAP_OPTIONS}
                    onOptionSelected={(discreteOption) => {
                      pickTailwindClassForProperty({
                        option: discreteOption.option,
                        propertyType: PropertyType.FLEX_WRAP,
                        setTemporaryDraggingValue:
                          flexWrapState.setTemporaryDraggingValue,
                      });
                    }}
                    selected={flexWrapState.propertyValue}
                    set={flexWrapState.isSet}
                    onDeselect={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FLEX_WRAP,
                        setTemporaryDraggingValue:
                          flexWrapState.setTemporaryDraggingValue,
                      });
                    }}
                  />
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent className="p-0 border-none">
                <VariantControl
                  valueByVariant={flexWrapState.valueByVariant as any}
                  onVariantChange={() => {}}
                  onClearVariant={(variant: VariantType) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FLEX_WRAP,
                      setTemporaryDraggingValue:
                        flexWrapState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  currentFlexDirection={flexDirectionState.propertyValue}
                  currentJustifyContent={justifyContentState.propertyValue}
                  currentAlignItems={alignItemsState.propertyValue}
                  tooltipContent="Variants"
                  className="mr-1"
                  controlType="direction-wrap"
                  children={(variant, expanded, setExpanded) => {
                    if (!expanded || !variant) return null;
                    const variantValue =
                      flexWrapState.valueByVariant[variant]?.value || "";
                    return (
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                          <DiscreteInput
                            options={FLEX_WRAP_OPTIONS}
                            onOptionSelected={(discreteOption) => {
                              pickTailwindClassForProperty({
                                option: discreteOption.option,
                                propertyType: PropertyType.FLEX_WRAP,
                                setTemporaryDraggingValue:
                                  flexWrapState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            selected={variantValue}
                            set={!!variantValue}
                            onDeselect={() => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.FLEX_WRAP,
                                setTemporaryDraggingValue:
                                  flexWrapState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                          />
                        </div>
                        <div className="flex flex-row gap-2 mt-2">
                          <div
                            className="text-xs text-secondary-n-500 hover:text-red-500"
                            onClick={() => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.FLEX_WRAP,
                                setTemporaryDraggingValue:
                                  flexWrapState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                          >
                            Clear
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </ContextMenuContent>
            </ContextMenu>
          </div>
        </div>
      </div>
    );
  },
);

export default FlexDirectionWrapField;
